import { feathers } from "@feathersjs/feathers";
import socketio from "@feathersjs/socketio-client";
import authentication from "@feathersjs/authentication-client";
import io from "socket.io-client";
import type { Application, TransportConnection } from "@feathersjs/feathers";
import { attachConnectionTelemetry } from "./connectionStatus";
// import rest from '@feathersjs/rest-client';

// Assuming createClient is a function you need to import from a specific module
// import { createClient } from '../../api/src/client'; // Adjust the path as necessary
declare const window: Window;

interface ConnectionInstances {
  [key: string]: Application; // Add an index signature
}

const createClient = (
  connection: TransportConnection,
  authenticationOptions = {}
) => {
  const client = feathers();

  client.configure(connection);
  client.configure(authentication(authenticationOptions));
  client.set("connection", connection);
  return client;
};

const connectionInstances: ConnectionInstances = {};

const devUri = "http://localhost:3130";
const stageUri = "https://stage.premier-api.intrasync.app";
const prodUri = "https://premier-api.intrasync.app";

type Mode =
  | "dev"
  | "development"
  | "stage"
  | "beta"
  | "live"
  | "prod"
  | "production"
  | "test";

const uriMap: Record<Mode, string> = {
  dev: devUri,
  development: devUri,
  stage: stageUri,
  beta: stageUri,
  live: prodUri,
  prod: prodUri,
  production: prodUri,
  test: "",
};

interface ApiOptions {
  debugMode?: boolean;
  authStorageKey?: string;
  refreshStorageKey?: string;
  enableSocketAuth?: boolean;
  restClient?: boolean;
  enableFindOne?: boolean;
  storage?: Storage | LocalForage;
  uriOverride?: Record<string, string>;
}

interface SocketOptions {
  path: string;
  transports: string[];
  /** engine.io: on a transport error, shift to the next entry in `transports`. */
  tryAllTransports?: boolean;
  forceNew: boolean;
  pingInterval: number;
  pingTimeout: number;
  timeout?: number;
  query?: {
    Authorization?: string;
  };
}

interface FirstLoadAuth {
  accessToken?: string;
  refreshToken?: string;
  deviceId?: string;
  userId?: string;
  authPath?: string;
}

interface AuthOptions {
  path?: string;
  storageKey: string;
  storage: Storage | LocalForage;
}

const apiClient = (
  mode: Mode,
  apiOptions: ApiOptions = {
    debugMode: false,
    authStorageKey: "feathers-jwt-dev",
    refreshStorageKey: "refresh-token-dev",
    enableSocketAuth: true,
    restClient: false,
    enableFindOne: true,
  }
): Application => {
  let socketAuthentication = false;
  const {
    debugMode,
    authStorageKey,
    refreshStorageKey,
    storage,
    uriOverride,
    // enableFindOne,
  } = apiOptions;

  if (debugMode) {
    console.warn("apiClient debugMode enabled");
    console.warn("apiClient debugMode apiOptions", apiOptions);
  }
  // console.log('feathers in', mode, 'mode');

  if (debugMode) {
    console.warn("apiClient debugMode: authStorageKey", authStorageKey);
  }
  if (connectionInstances[mode]) {
    if (debugMode) {
      console.warn(
        "apiClient debugMode: connectionInstances[mode]",
        mode,
        connectionInstances[mode]
      );
    }
    return connectionInstances[mode];
  } else {
    if (mode === "test") {
      connectionInstances[mode] = feathers().configure(authentication());
      if (debugMode) {
        console.warn(
          "apiClient debugMode: connectionInstances[mode]",
          mode,
          connectionInstances[mode]
        );
      }
      return connectionInstances[mode];
    }
    let uri = uriMap[mode];
    if (debugMode) {
      console.warn("apiClient debugMode: uri", uri);
    }
    if (uriOverride && Object.keys(uriOverride).length > 0) {
      const uriWithOverrides = { ...uriMap, ...uriOverride };
      uri = uriWithOverrides[mode];
      if (debugMode) {
        console.warn("apiClient debugMode: uriOverride set uri", uri);
      }
    }
    const authStorage = storage ? storage : window.localStorage;
    if (debugMode) {
      console.warn("apiClient debugMode: authStorage", authStorage);
    }
    let authOptions: AuthOptions = {
      storageKey: authStorageKey || "intrasync-jwt-dev",
      storage: authStorage,
    };

    const authPath = `/authentication`; // defaults to /authentication
    if (debugMode) {
      console.warn(
        "apiClient debugMode: Adding authPath to authOptions.path",
        authPath
      );
    }
    authOptions = {
      ...authOptions,
      // path: authPath,
    };

    if (debugMode) {
      console.warn("apiClient debugMode: authOptions", authOptions);
    }

    // if (apiOptions.restClient) {
    //   const apiRestClient = rest(uri);

    //   connectionInstances[mode] = feathers()
    //     .configure(apiRestClient.fetch(fetch))
    //     .configure(authentication(authOptions));

    //   if (debugMode) {
    //     console.warn(
    //       'apiClient debugMode: connectionInstances[mode]',
    //       mode,
    //       connectionInstances[mode]
    //     );
    //   }
    //   return connectionInstances[mode];
    // }

    let socketOptions: SocketOptions = {
      path: "/socket.io",
      // WebSocket first, HTTP long-polling as a fallback.
      //
      // The client used to be websocket-only, which meant that any network
      // refusing the WebSocket upgrade produced a total failure rather than a
      // slow connection — the app hangs on "still loading", which is exactly
      // what Premier reported on 2026-09-14 and 09-17 after their IT rolled
      // out a Fortinet VPN. SSL-inspecting proxies commonly break the upgrade.
      //
      // The happy path is unchanged: websocket is still attempted first, so a
      // healthy client connects over WebSocket exactly as before. Only a
      // FAILED upgrade falls back, and only because tryAllTransports is set —
      // without it engine.io stops at the first transport error instead of
      // shifting to the next (engine.io-client/build/cjs/socket.js:512).
      transports: ["websocket", "polling"],
      tryAllTransports: true,
      forceNew: true,
      pingInterval: 25000, // Increased for stability
      pingTimeout: 60000, // Increased timeout
      timeout: 10000, // Increased connection timeout
    };
    if (debugMode) {
      console.warn("apiClient debugMode: socketOptions", socketOptions);
    }
    if (apiOptions.enableSocketAuth && window && window.localStorage) {
      if (debugMode) {
        console.warn("apiClient debugMode: window", window);
      }

      try {
        // Batch localStorage reads for better performance
        const userIdKey = mode.includes("prod") ? "userId" : `userId-${mode}`;
        const [accessToken, refreshToken, deviceId, userId] = [
          window.localStorage.getItem(authStorageKey || "feathers-jwt-dev"),
          window.localStorage.getItem(refreshStorageKey || "refresh-token-dev"),
          window.localStorage.getItem("deviceId"),
          window.localStorage.getItem(userIdKey),
        ];
        // Retrieved auth data from localStorage
        if (debugMode) {
          console.warn("apiClient debugMode: batched auth data", {
            hasAccessToken: !!accessToken,
            hasRefreshToken: !!refreshToken,
            hasDeviceId: !!deviceId,
            hasUserId: !!userId,
          });
        }

        // Validate tokens before using them
        const isValidToken = (token: string | null): boolean => {
          if (!token) return false;
          try {
            // Basic JWT validation - check if it has proper structure
            const parts = token.split(".");
            return parts.length === 3 && parts.every((part) => part.length > 0);
          } catch {
            return false;
          }
        };

        const firstLoadAuth: FirstLoadAuth = {};

        if (isValidToken(accessToken)) {
          firstLoadAuth.accessToken = accessToken!;
        }

        if (isValidToken(refreshToken)) {
          firstLoadAuth.refreshToken = refreshToken!;
        }

        if (deviceId) {
          firstLoadAuth.deviceId = deviceId;
        }

        if (userId) {
          firstLoadAuth.userId = userId;
        }

        if (authOptions && authOptions.path) {
          firstLoadAuth.authPath = authOptions.path;
        }

        // Only setup socket auth if we have minimum required data
        if (
          firstLoadAuth.deviceId &&
          (firstLoadAuth.refreshToken || firstLoadAuth.accessToken)
        ) {
          try {
            const Auth = JSON.stringify(firstLoadAuth);
            // Limit auth payload size to prevent socket issues
            if (Auth.length < 2048) {
              socketOptions = {
                ...socketOptions,
                query: {
                  Authorization: Auth,
                },
              };
              socketAuthentication = true;

              if (debugMode) {
                console.warn("apiClient debugMode: socket auth enabled", {
                  authPayloadSize: Auth.length,
                  hasToken: !!firstLoadAuth.accessToken,
                  hasRefresh: !!firstLoadAuth.refreshToken,
                });
              }
            } else {
              console.warn(
                "apiClient: Auth payload too large, skipping socket auth"
              );
            }
          } catch (error) {
            console.warn("apiClient: Failed to serialize auth data:", error);
          }
        } else {
          if (debugMode) {
            console.warn(
              "apiClient debugMode: insufficient auth data for socket auth",
              {
                hasDeviceId: !!firstLoadAuth.deviceId,
                hasTokens: !!(
                  firstLoadAuth.refreshToken || firstLoadAuth.accessToken
                ),
              }
            );
          }
        }
      } catch (error) {
        console.warn(
          "apiClient: Error setting up socket authentication:",
          error
        );
      }
    }

    const socket = io(uri, socketOptions);

    // Roadmap MPL row 1 ("Reconnection Issues"). Lifecycle telemetry +
    // connection state, attached UNCONDITIONALLY. The handlers below used to
    // be the only ones on this socket and they were wrapped in
    // `if (debugMode)` — so in production, the environment where Josh's
    // tablets actually drop, nothing observed the connection at all. See
    // connectionStatus.ts for the three candidate causes this is meant to
    // tell apart.
    attachConnectionTelemetry(socket);

    // Verbose console mirror, still debug-only.
    if (debugMode) {
      socket.on("connect", () => {
        console.warn("apiClient debugMode: Socket connected");
      });

      socket.on("disconnect", (reason) => {
        console.warn("apiClient debugMode: Socket disconnected:", reason);
      });

      socket.on("connect_error", (error) => {
        console.warn("apiClient debugMode: Socket connection error:", error);
      });
    }

    if (debugMode) {
      console.warn("apiClient debugMode: socket", socket);
    }

    const socketConnection = socketio(socket, {
      timeout: 100000,
    });

    // connectionInstances[mode] = feathers()
    //   .configure(
    //     socketio(socket, {
    //       timeout: 100000,
    //     })
    //   )
    //   .configure(authentication(authOptions));

    const newClient = createClient(socketConnection, authOptions);

    // Only log in debug mode
    if (debugMode) {
      console.warn("apiClient debugMode: newClient", newClient);
    }
    connectionInstances[mode] = newClient;

    // if (enableFindOne) {
    //   connectionInstances[mode].mixins.push(function (service: any) {
    //     service.findOne = function (params: any) {
    //       params = params || {};
    //       params.query = params.query || {};
    //       params.query.$limit = 1;
    //       return service.find(params).then(function (result: any) {
    //         const data = result.data || result;
    //         return Array.isArray(data) ? data[0] : data;
    //       });
    //     };
    //   });
    // }

    if (debugMode) {
      console.warn(
        "apiClient debugMode: connectionInstances[mode]",
        mode,
        connectionInstances[mode]
      );
    }
    if (
      socketAuthentication &&
      connectionInstances[mode] &&
      "set" in connectionInstances[mode]
    ) {
      connectionInstances[mode].set("socketAuthPending", true);
      if (debugMode) {
        console.warn(
          'apiClient debugMode: connectionInstances[mode].set("socketAuthPending", true);'
        );
      }
    }
    return connectionInstances[mode];
  }
};

/**
 * export default apiClient
 * @param {String} mode Server Mode: dev, development, stage, beta, prod, production
 * @param {Object} apiOptions
 *  Default:
 *  {
 *    debugMode: false,
 *    authStorageKey: 'feathers-jwt-dev',
 *    refreshStorageKey: 'refresh-token-dev',
 *    enableSocketAuth: true,
 *    restClient: false,
 *    enableFindOne: false
 *  }
 * @returns Api Client
 */
export default apiClient;
